import { FC, useCallback, useMemo } from 'react';

import { AdditionalDefault, ChipsContainer, CustomChip, FormLabel, TreeModel, VSpacing } from '@hh.ru/magritte-ui';

import { useCompositeSelectionContext } from 'components/common/CompositeSelection/contexts';

interface BaseChipsProps {
    sourceValue: string[];
    onDelete: (id: string) => void;
    containerId: string;
    containerDataQa: string;
    size?: 'small' | 'extra-small';
    title?: string;
}

const BaseChips: FC<BaseChipsProps> = ({
    sourceValue,
    onDelete,
    containerId,
    containerDataQa,
    size = 'small',
    title,
}) => {
    const { collection } = useCompositeSelectionContext();

    const models = useMemo(() => {
        if (!sourceValue || sourceValue.length === 0) {
            return [];
        }

        return sourceValue.reduce<TreeModel<AdditionalDefault>[]>((acc, curr) => {
            const model = collection.getModel(curr);

            if (model) {
                acc.push(model);
            }

            return acc;
        }, []);
    }, [collection, sourceValue]);

    const handleDelete = useCallback(
        (id: string) => {
            onDelete(id);
        },
        [onDelete]
    );

    if (models.length === 0) {
        return null;
    }

    return (
        <div>
            {title && (
                <>
                    <FormLabel htmlFor={containerId}>{title}</FormLabel>
                    <VSpacing default={12} />
                </>
            )}
            <div id={containerId}>
                <ChipsContainer correction={8} data-qa={containerDataQa}>
                    {models.map((model) => (
                        <CustomChip
                            size={size}
                            key={model.id}
                            Element="button"
                            onDelete={() => handleDelete(model.id)}
                            deleteOnly
                        >
                            {model.text}
                        </CustomChip>
                    ))}
                </ChipsContainer>
            </div>
        </div>
    );
};

export { BaseChips };
